import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, Download, RefreshCw, AlertTriangle, Flag, Send, Check, Paperclip, X, Mail, FileWarning, FileArchive } from 'lucide-react';
import { bankAPI, downloadBlob } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

function fmt(v) {
    return Number(v || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const KIND_LABEL = {
    matched: 'Bank · matched', bank_only: 'Bank · unmatched',
    owner_movement: 'Owner movement', privately_paid: 'Privately paid',
};

function coverageBadge(status) {
    switch (status) {
        case 'delivered':
            return <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Check className="h-3 w-3" /> delivered</span>;
        case 'ready':
            return <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Mail className="h-3 w-3" /> ready</span>;
        case 'no_document':
            return <span className="text-xs text-red-700 font-medium inline-flex items-center gap-1"><FileWarning className="h-3 w-3" /> NO document</span>;
        case 'no_bilag':
            return <span className="text-xs text-muted-foreground/70">no bilag — nothing to deliver</span>;
        case 'unsupported':
            return <span className="text-xs text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> unsupported type</span>;
        case 'too_large':
            return <span className="text-xs text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> too large (&gt;9 MB)</span>;
        default:
            return <span className="text-xs text-muted-foreground">{status}</span>;
    }
}

export default function BankExportPage() {
    const { currentTenant } = useTenant();
    const [fyOptions, setFyOptions] = useState([]);
    const [fy, setFy] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // e-conomic Plus push state
    const [plus, setPlus] = useState(null);            // preview payload (config + per-bilag status)
    const [pushing, setPushing] = useState(false);
    const [pushStatus, setPushStatus] = useState({});  // bilag -> { status, voucher_number, attachment_present, error }
    const [plusNote, setPlusNote] = useState(null);    // fallback / summary message after a push
    // Basic-tier inbox delivery state
    const [basis, setBasis] = useState(null);          // coverage preview (rows + summary + config)
    const [delivering, setDelivering] = useState(false);

    useEffect(() => {
        if (!currentTenant) return;
        bankAPI.getLines(currentTenant.id)
            .then((r) => {
                const years = r.data.fiscal_years || [];
                setFyOptions(years);
                setFy((prev) => prev || (years.length ? String(years[years.length - 1]) : null));
            })
            .catch(() => {});
    }, [currentTenant]);

    const load = useCallback(async () => {
        if (!currentTenant || !fy) { setData(null); return; }
        setLoading(true);
        try {
            const r = await bankAPI.getExport(currentTenant.id, fy);
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load export');
        } finally { setLoading(false); }
    }, [currentTenant, fy]);

    useEffect(() => { load(); }, [load]);

    const download = async () => {
        setDownloading(true);
        try {
            const r = await bankAPI.downloadKassekladde(currentTenant.id, fy);
            downloadBlob(r, `kassekladde_${fy}.csv`);
            toast.success('Kassekladde CSV downloaded');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Download failed');
        } finally { setDownloading(false); }
    };

    const isPlus = data?.delivery?.target === 'economic_plus';

    // Load the read-only Plus preview (config + per-entry push status) for economic_plus clients.
    const loadPlus = useCallback(async () => {
        if (!currentTenant || !fy || !isPlus) { setPlus(null); return; }
        try {
            const r = await bankAPI.pushPlusPreview(currentTenant.id, fy);
            setPlus(r.data);
            const seed = {};
            (r.data.rows || []).forEach((row) => {
                if (row.pushed) seed[row.bilag] = { status: 'pushed', voucher_number: row.voucher_number, attachment_present: row.attachment_present };
            });
            setPushStatus(seed);
        } catch (e) { setPlus(null); }
    }, [currentTenant, fy, isPlus]);

    useEffect(() => { loadPlus(); setPlusNote(null); }, [loadPlus]);

    // Push DRAFT vouchers to e-conomic. bilag=null -> all not-yet-pushed; otherwise the given subset.
    const doPush = async (bilag = null) => {
        setPushing(true); setPlusNote(null);
        try {
            const r = await bankAPI.pushPlus(currentTenant.id, fy, bilag);
            const res = r.data;
            if (res.fallback === 'csv') {
                setPlusNote({ kind: 'fallback', text: res.message });
                toast.error('e-conomic push unavailable — falling back to CSV');
            } else {
                const next = { ...pushStatus };
                (res.results || []).forEach((x) => { next[x.bilag] = x; });
                setPushStatus(next);
                const parts = [`${res.pushed} pushed`];
                if (res.failed) parts.push(`${res.failed} failed`);
                if (res.skipped) parts.push(`${res.skipped} already pushed`);
                setPlusNote({ kind: 'summary', text: `Drafts created in journal ${res.journal_number} (range ${res.voucher_range?.min ?? '—'}–${res.voucher_range?.max ?? '∞'}): ${parts.join(', ')}. Review & book in e-conomic.` });
                (res.failed ? toast.error : toast.success)(parts.join(' · '));
            }
            loadPlus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Push failed');
        } finally { setPushing(false); }
    };

    const isBasis = data?.delivery?.target === 'economic_basis';

    // Load the Basic-tier inbox-delivery coverage (per-bilag status + summary) for Basic clients.
    const loadBasis = useCallback(async () => {
        if (!currentTenant || !fy || !isBasis) { setBasis(null); return; }
        try {
            const r = await bankAPI.deliverInboxPreview(currentTenant.id, fy);
            setBasis(r.data);
        } catch (e) { setBasis(null); }
    }, [currentTenant, fy, isBasis]);

    useEffect(() => { loadBasis(); }, [loadBasis]);

    // Email every deliverable bilag to the client's e-conomic scan inbox (one email per bilag).
    const doDeliver = async (force = false) => {
        setDelivering(true);
        try {
            const r = await bankAPI.deliverInbox(currentTenant.id, fy, { force });
            const c = r.data.counts || {};
            const bits = [`${c.delivered || 0} delivered`];
            if (c.skipped) bits.push(`${c.skipped} already`);
            if (c.no_document) bits.push(`${c.no_document} missing document`);
            if (c.unsupported || c.too_large) bits.push(`${(c.unsupported || 0) + (c.too_large || 0)} unsupported`);
            if (c.failed) bits.push(`${c.failed} failed`);
            (c.failed || c.no_document ? toast.warning : toast.success)(bits.join(' · '));
            loadBasis();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delivery failed');
        } finally { setDelivering(false); }
    };

    // Per-bilag recovery actions (no e-conomic dependency).
    const downloadOneBilag = async (bilag) => {
        try {
            const r = await bankAPI.downloadBilag(currentTenant.id, fy, bilag);
            downloadBlob(r, `${bilag}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Could not download bilag ${bilag}`);
        }
    };
    const redeliverOne = async (bilag) => {
        try {
            const r = await bankAPI.deliverInbox(currentTenant.id, fy, { bilag: [bilag], force: true });
            const c = r.data.counts || {};
            (c.delivered ? toast.success : toast.error)(c.delivered ? `Bilag ${bilag} re-sent to inbox` : `Bilag ${bilag} not sent`);
            loadBasis();
        } catch (e) {
            toast.error(e.response?.data?.detail || `Could not re-deliver bilag ${bilag}`);
        }
    };
    const downloadZip = async () => {
        try {
            const r = await bankAPI.downloadBilagZip(currentTenant.id, fy);
            downloadBlob(r, `bilag_${fy}.zip`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not build the zip');
        }
    };

    if (!currentTenant) {
        return (
            <div className="space-y-6" data-testid="bank-export-page">
                <h1 className="font-heading text-3xl font-bold">Bank export</h1>
                <Card><CardContent className="py-12 text-center text-muted-foreground">Select a company to review the export.</CardContent></Card>
            </div>
        );
    }

    const s = data?.summary;
    const rows = data?.rows || [];
    const needs = data?.needs_attention || [];
    const delivery = data?.delivery;
    // bilag of complete entries not yet pushed (drives the push buttons; respects idempotency)
    const notPushedBilag = rows
        .map((r) => Number(r.bilag))
        .filter((b) => pushStatus[b]?.status !== 'pushed');

    return (
        <div className="space-y-8" data-testid="bank-export-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <FileSpreadsheet className="h-7 w-7 text-primary" /> Bank export
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {currentTenant.name} — complete double-entry kassekladde, ready to import into e-conomic. Incomplete entries are held back below.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={fy || ''} onValueChange={setFy}>
                        <SelectTrigger className="h-9 w-36" data-testid="export-fy"><SelectValue placeholder="Fiscal year" /></SelectTrigger>
                        <SelectContent>
                            {fyOptions.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={load} title="Recompute" data-testid="export-refresh">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    {isPlus && (
                        <Button
                            onClick={() => doPush(null)}
                            disabled={pushing || !plus?.configured || notPushedBilag.length === 0}
                            title={!plus?.configured ? 'Configure e-conomic grant token + journal first' : 'Push all not-yet-pushed complete entries directly into e-conomic, each with its bilag attached'}
                            data-testid="push-plus-all">
                            <Send className={`h-4 w-4 mr-1 ${pushing ? 'animate-pulse' : ''}`} /> Push to e-conomic
                        </Button>
                    )}
                    {isBasis && (
                        <Button
                            onClick={() => doDeliver(false)}
                            disabled={delivering || !basis?.configured || !(basis?.summary?.ready)}
                            title={!basis?.configured ? 'Set this client\'s e-conomic scan-inbox email first' : 'Email each numbered bilag to the e-conomic inbox'}
                            data-testid="deliver-inbox">
                            <Mail className={`h-4 w-4 mr-1 ${delivering ? 'animate-pulse' : ''}`} /> Deliver bilag to e-conomic inbox
                        </Button>
                    )}
                    <Button variant={(isPlus || isBasis) ? 'ghost' : 'default'} onClick={download} disabled={downloading || rows.length === 0} data-testid="export-download">
                        <Download className="h-4 w-4 mr-1" /> Download CSV{isPlus ? ' (fallback)' : ''}
                    </Button>
                </div>
            </div>

            {!fy ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                    No bank lines yet — import a statement on the Bank statements screen first.
                </CardContent></Card>
            ) : (
                <>
                    {s && (
                        <div className="flex flex-wrap gap-3 text-sm items-center">
                            <Badge variant="success">Ready to book {s.complete}</Badge>
                            <Badge variant="outline" className="border-amber-400 text-amber-700">Needs attention {s.needs_attention}</Badge>
                            {s.pending_codes > 0 && <Badge variant="secondary"><Flag className="h-3 w-3 mr-1" />{s.pending_codes} provisional VAT codes</Badge>}
                            {delivery?.label && <span className="text-muted-foreground self-center">delivery: {delivery.label}</span>}
                        </div>
                    )}

                    {/* Plain-language per-tier explainer (Step 3) — what happens for THIS client */}
                    {isBasis && (
                        <div className="text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3" data-testid="tier-explainer-basis">
                            <strong>This client is on e-conomic Basis (read-only).</strong> Accountrix gives you a CSV to import into the kassekladde and emails the numbered bilag to the inbox; you attach each numbered document to its line in e-conomic.
                        </div>
                    )}
                    {isPlus && (
                        <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3" data-testid="tier-explainer-plus">
                            <strong>This client is on e-conomic Plus.</strong> Accountrix pushes the finished entries directly into e-conomic with each bilag attached automatically — no CSV, no manual attaching. (CSV is still available as a fallback.)
                        </div>
                    )}
                    {!isBasis && !isPlus && delivery?.note && (
                        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3" data-testid="tier-explainer-other">
                            {delivery.note}
                        </div>
                    )}
                    {delivery?.note && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{delivery.note}</div>
                    )}
                    {isPlus && plus && !plus.configured && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                            e-conomic Plus push not configured — set the agreement grant token and journal number for this client{!plus.app_secret_set ? ', and the app secret on the server' : ''}. Until then, use Download CSV.
                        </div>
                    )}
                    {plusNote && (
                        <div className={`text-xs rounded-md p-2 border ${plusNote.kind === 'fallback' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-emerald-800 bg-emerald-50 border-emerald-200'}`} data-testid="plus-note">
                            {plusNote.text}
                        </div>
                    )}

                    {/* Basic-tier inbox delivery: plain note + per-bilag coverage */}
                    {isBasis && (
                        <>
                            <div className="text-xs text-muted-foreground bg-muted/50 border rounded-md p-3">
                                Accountrix emails the numbered bilag to your e-conomic inbox{basis?.inbox ? <> (<span className="font-mono">{basis.inbox}</span>)</> : ''}. In e-conomic, click each numbered document onto its matching line — on Basic that final click is manual because Basic blocks outside writing. (Higher tier attaches automatically.)
                                {!basis?.configured && <span className="block mt-1 text-amber-700">No scan-inbox email set for this client yet — add it on the company’s accounting profile to enable delivery.</span>}
                            </div>
                            {basis && (
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between">
                                        <CardTitle className="text-lg">Bilag coverage</CardTitle>
                                        <div className="flex items-center gap-2 text-sm">
                                            <Badge variant="success">{(basis.summary.delivered) + ' of ' + basis.summary.deliverable + ' bilag delivered'}</Badge>
                                            {basis.summary.no_document > 0 && (
                                                <Badge variant="outline" className="border-red-400 text-red-700">
                                                    <FileWarning className="h-3 w-3 mr-1" />{basis.summary.no_document} with NO document
                                                </Badge>
                                            )}
                                            {(basis.summary.unsupported + basis.summary.too_large) > 0 && (
                                                <Badge variant="outline" className="border-amber-400 text-amber-700">{basis.summary.unsupported + basis.summary.too_large} unsupported</Badge>
                                            )}
                                            <Button variant="outline" size="sm" onClick={downloadZip} title="Download every bilag as a zip (numbered filenames)">
                                                <FileArchive className="h-3 w-3 mr-1" /> Download all bilag (zip)
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => doDeliver(true)} disabled={delivering || !basis.configured} title="Re-send all (even already-delivered)">
                                                <RefreshCw className={`h-3 w-3 mr-1 ${delivering ? 'animate-spin' : ''}`} /> Re-deliver all
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="overflow-x-auto">
                                        <p className="text-xs text-muted-foreground mb-3">
                                            Delivered = Accountrix emailed the bilag to the e-conomic inbox. Once you attach a bilag to a line in e-conomic it leaves the inbox; if you need it again, re-deliver or download it here.
                                        </p>
                                        <table className="w-full text-sm" data-testid="bilag-coverage">
                                            <thead>
                                                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                                                    <th className="py-2 pr-3">Bilag</th><th className="py-2 pr-3">Vendor</th>
                                                    <th className="py-2 pr-3 text-right">Beløb</th><th className="py-2 pr-3">Status</th>
                                                    <th className="py-2 pr-3">File</th><th className="py-2 pr-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {basis.rows.map((r) => {
                                                    const hasFile = ['delivered', 'ready', 'unsupported', 'too_large'].includes(r.status);
                                                    const canRedeliver = ['delivered', 'ready'].includes(r.status) && basis.configured;
                                                    return (
                                                        <tr key={r.bilag} className={`border-b last:border-0 ${r.status === 'no_document' ? 'bg-red-50' : ''}`} data-testid={`cov-${r.bilag}`}>
                                                            <td className="py-2 pr-3 font-mono font-semibold">{r.bilag}</td>
                                                            <td className="py-2 pr-3 max-w-[16rem] truncate">{r.vendor}</td>
                                                            <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">{fmt(r.amount)} {r.currency}</td>
                                                            <td className="py-2 pr-3">{coverageBadge(r.status)}</td>
                                                            <td className="py-2 pr-3 font-mono text-xs text-muted-foreground truncate max-w-[14rem]">{r.filename || '—'}</td>
                                                            <td className="py-2 pr-3">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <Button variant="ghost" size="sm" className="h-7 px-2" disabled={!hasFile}
                                                                        onClick={() => downloadOneBilag(r.bilag)}
                                                                        title={hasFile ? `Download ${r.filename || 'original'}` : 'No stored document'}
                                                                        data-testid={`dl-${r.bilag}`}>
                                                                        <Download className="h-3 w-3" />
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" className="h-7 px-2" disabled={!canRedeliver}
                                                                        onClick={() => redeliverOne(r.bilag)}
                                                                        title={canRedeliver ? 'Re-send this bilag to the e-conomic inbox' : (basis.configured ? 'Nothing deliverable for this row' : 'No scan-inbox email set')}
                                                                        data-testid={`rd-${r.bilag}`}>
                                                                        <Mail className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}

                    {/* Materialized kassekladde rows */}
                    <Card>
                        <CardHeader><CardTitle className="text-lg">Kassekladde — ready to book ({rows.length})</CardTitle></CardHeader>
                        <CardContent className="overflow-x-auto">
                            {rows.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">No complete entries yet.</p>
                            ) : (
                                <table className="w-full text-sm" data-testid="kassekladde-table">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                                            <th className="py-2 pr-3">Bilag</th><th className="py-2 pr-3">Dato</th>
                                            <th className="py-2 pr-3">Konto</th><th className="py-2 pr-3">Modkonto</th>
                                            <th className="py-2 pr-3 text-right">Beløb</th><th className="py-2 pr-3">Momskode</th>
                                            <th className="py-2 pr-3">Tekst</th><th className="py-2 pr-3">Type</th>
                                            {isPlus && <th className="py-2 pr-3">e-conomic</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} className="border-b last:border-0" data-testid={`kk-row-${r.bilag}`}>
                                                <td className="py-2 pr-3 font-mono">{r.bilag}</td>
                                                <td className="py-2 pr-3 font-mono whitespace-nowrap">{r.date}</td>
                                                <td className="py-2 pr-3 font-mono font-semibold">{r.account}</td>
                                                <td className="py-2 pr-3 font-mono">{r.contra}</td>
                                                <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">{fmt(r.amount)} {r.currency}</td>
                                                <td className="py-2 pr-3 font-mono">
                                                    <span className={r.pending ? 'text-amber-700' : ''}>{r.vat_code || '—'}</span>
                                                    {r.pending && <Flag className="inline h-3 w-3 ml-1 text-amber-600" title={r.pending_note || 'Pending umar confirmation'} />}
                                                </td>
                                                <td className="py-2 pr-3 max-w-[18rem] truncate">{r.text}</td>
                                                <td className="py-2 pr-3"><Badge variant="outline" className="whitespace-nowrap">{KIND_LABEL[r.kind] || r.kind}</Badge></td>
                                                {isPlus && (
                                                    <td className="py-2 pr-3 whitespace-nowrap">
                                                        {(() => {
                                                            const st = pushStatus[Number(r.bilag)];
                                                            if (!st) return <span className="text-muted-foreground text-xs">—</span>;
                                                            if (st.status === 'pushed') return (
                                                                <span className="text-xs text-emerald-700 inline-flex items-center gap-1" title={st.attachment_error || ''}>
                                                                    <Check className="h-3 w-3" /> #{st.voucher_number ?? '?'}
                                                                    {st.attachment_present
                                                                        ? <Paperclip className="h-3 w-3 text-emerald-600" title="bilag attached" />
                                                                        : <Paperclip className="h-3 w-3 text-muted-foreground/40" title={st.attachment_error || 'no bilag'} />}
                                                                </span>
                                                            );
                                                            if (st.status === 'skipped') return <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Check className="h-3 w-3" /> #{st.voucher_number ?? '—'}</span>;
                                                            return <span className="text-xs text-red-600 inline-flex items-center gap-1" title={st.error || ''}><X className="h-3 w-3" /> failed</span>;
                                                        })()}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {s?.pending_codes > 0 && (
                                <p className="text-xs text-amber-700 mt-3 flex items-center gap-1">
                                    <Flag className="h-3 w-3" /> Rows marked with a flag carry a reverse-charge code that is provisional — pending accountant (umar) confirmation.
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Needs attention — excluded from export */}
                    <Card>
                        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Needs attention before booking ({needs.length})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            <p className="text-xs text-muted-foreground">These entries are one-sided or unconfirmed and are NOT exported — resolve them on the coding / matching screens first.</p>
                            {needs.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">None — every entry is complete.</p>
                            ) : needs.map((n, i) => (
                                <div key={i} className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`needs-${i}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{n.ref || '—'}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{n.date} · {fmt(n.amount)}</div>
                                    </div>
                                    <span className="text-xs text-amber-700">{n.reason}</span>
                                    <Badge variant="outline" className="whitespace-nowrap">{KIND_LABEL[n.kind] || n.kind}</Badge>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
